import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Nav,
  Container,
  Row,
  Col,
  Button,
  Badge,
  Spinner,
  Tab,
  Card,
  Form,
  Modal,
} from 'react-bootstrap'
import { useAppSelector, useAppDispatch } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import {
  useGetModelQuery,
  useFetchModelsQuery,
  useDeployModelMutation,
  useDeleteModelDeploymentMutation,
  useDeleteModelMutation,
  useUpdateModelMutation,
  useStopTrainModelMutation,
  useGetDefaultHyperparametersQuery,
} from '../../../services/modelsApi'
import { useGetTagsQuery } from '../../../services/tagApi'
import { useGetProjectQuery } from '../../../services/projectsApi'
import { useAnnotationStats } from '../../../store/hooks'
import {
  ArrowLeft,
  Brain,
  Zap,
  BarChart3,
  Database,
  Rocket,
  Settings,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  TrendingUp,
  ArrowUp,
  Edit2,
  Check,
  X,
  ArrowDown,
  Package,
  Tag,
  Box,
  Star,
  Trash2,
  GitBranch,
  ArrowLeftRight,
  Square,
} from 'lucide-react'
import styled from 'styled-components'
import ConfirmDialog from '../../modals/ConfirmDialog'
import ErrorDialog from '../../modals/ErrorDialog'
import { PageWithTabs } from '../../common'
import { ModelUpdateParams } from '../../../types/api/api-model'

// Import existing components for different tabs
import ModelTrainingView from '../ModelTrainingView/ModelTrainingView'
import ModelPerformanceView from '../ModelPerformanceView'
import ModelGenerationView from '../ModelGenerationView/ModelGenerationView'
import ModelDeploymentView from '../ModelDeploymentView/ModelDeploymentView'
import ModelDatasetsView from '../ModelDatasetView'
import ModelVersionsView from '../ModelVersionsView'
import ModelComparisonView from '../ModelComparisonView/ModelComparisonView'
import CombinedTrainingPerformanceView from '../CombinedTrainingPerformanceView/CombinedTrainingPerformanceView'
import { formatDuration } from '../../../utils/timeUtils'

const DetailContainer = styled.div`
  background: transparent;
  min-height: 100vh;
  padding: 0;
`

const HeaderContainer = styled.div`
  background: transparent;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1.5rem 2rem;
`

const BackButton = styled.button`
  background: linear-gradient(135deg, rgba(128, 0, 133, 0.15) 0%, rgba(147, 51, 234, 0.15) 100%);
  border: 1px solid rgba(128, 0, 133, 0.3);
  color: #ffffff;
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.95rem;
  backdrop-filter: blur(10px);

  svg {
    transition: transform 0.3s ease;
  }

  &:hover {
    background: linear-gradient(135deg, rgba(128, 0, 133, 0.25) 0%, rgba(147, 51, 234, 0.25) 100%);
    border-color: rgba(128, 0, 133, 0.5);
    box-shadow: 0 4px 20px rgba(128, 0, 133, 0.2);
    transform: translateX(-3px);

    svg {
      transform: translateX(-3px);
    }
  }

  &:active {
    transform: translateX(0px);
  }
`

const ModelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`

const ModelIcon = styled.div<{ $type: string }>`
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);

  ${(props) =>
    props.$type === 'classification' &&
    `
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  `}

  ${(props) =>
    props.$type === 'object-detection' &&
    `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  `}

  ${(props) =>
    props.$type === 'oriented_bounding_box' &&
    `
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  `}
`

const ModelInfo = styled.div`
  flex: 1;
`

const ModelName = styled.h1`
  color: #ffffff;
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
`

const ModelDescription = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 1rem;
  margin: 0 0 1rem 0;
`

const ModelMeta = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
`

const StatusBadge = styled(Badge)<{ $status: string }>`
  font-size: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${(props) => {
    switch (props.$status?.toLowerCase()) {
      case 'trained':
        return 'background: #10b981; color: white;'
      case 'training':
        return 'background: #f59e0b; color: white;'
      case 'initialized':
        return 'background: #6b7280; color: white;'
      case 'deployed':
        return 'background: #3b82f6; color: white;'
      case 'deploying':
        return 'background: #8b5cf6; color: white;'
      case 'failed':
        return 'background: #ef4444; color: white;'
      default:
        return 'background: #6b7280; color: white;'
    }
  }}
`

const NavContainer = styled.div`
  background: rgba(42, 42, 42, 0.6);
  border: none;
  border-radius: 12px;
  padding: 0.5rem;
  margin-bottom: 1.5rem;
  backdrop-filter: blur(10px);
  position: relative;
  overflow: visible;

  .nav-tabs {
    border-bottom: none;
    margin-bottom: 0;
    gap: 0.75rem;
    font-size: 0.9rem;
    display: flex;

    .nav-item {
      margin: 0;
      display: flex;
      align-items: center;
    }

    .nav-link {
      border: 1px solid transparent;
      background: rgba(60, 60, 60, 0.3);
      color: #a0a0a0;
      border-radius: 10px;
      padding: 0.75rem 1rem;
      transition: all 0.3s ease;
      margin: 0;
      position: relative;
      overflow: hidden;
      font-weight: 500;
      backdrop-filter: blur(5px);
      display: flex;
      align-items: center;
      gap: 0.5rem;

      &:hover {
        background: rgba(74, 74, 74, 0.6);
        border-color: rgba(102, 126, 234, 0.3);
        color: #ffffff;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }

      &.active {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border-color: #667eea;
        color: #ffffff;
        font-weight: 600;
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
        border-bottom: none;
      }

      &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;

        &:hover {
          transform: none;
        }
      }

      .tab-icon {
        font-size: 1.1rem;
      }
    }
  }
`

const TabContent = styled.div`
  padding: 2rem;
  background: transparent;
  min-height: calc(100vh - 300px);
`

const OverviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`

const Section = styled.div`
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid rgba(102, 126, 234, 0.2);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(102, 126, 234, 0.4);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }
`

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 1rem;
`

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`

const MetricCard = styled.div`
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 16px;
  padding: 1.25rem;
  transition: all 0.3s ease;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  text-align: center;
  position: relative;
  cursor: help;

  &:hover {
    border-color: rgba(102, 126, 234, 0.4);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }

  .metric-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    margin-bottom: 0.75rem;
    color: #ffffff;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
  }

  .metric-value {
    font-size: 1.75rem;
    font-weight: 700;
    color: #ffffff;
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    margin-bottom: 0.25rem;

    .metric-unit {
      font-size: 1rem;
      font-weight: 400;
      color: rgba(255, 255, 255, 0.6);
    }
  }

  .metric-label {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.6);
    margin-bottom: 0.75rem;
    font-weight: 500;
  }
`

const DatasetCard = styled.div<{ $type: 'training' | 'validation' }>`
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border-radius: 16px;
  padding: 1.25rem;
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-left: 4px solid ${(props) => (props.$type === 'training' ? '#10b981' : '#3b82f6')};
  transition: all 0.3s ease;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);

  &:hover {
    border-color: rgba(102, 126, 234, 0.4);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }

  .dataset-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .dataset-indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(props) => (props.$type === 'training' ? '#10b981' : '#3b82f6')};
  }

  .dataset-title {
    font-weight: 600;
    color: #ffffff;
  }

  .dataset-stats {
    display: flex;
    gap: 2rem;
    margin-top: 1rem;
  }

  .stat-item {
    text-align: center;
  }

  .stat-value {
    font-size: 1.25rem;
    font-weight: 700;
    color: #ffffff;
  }

  .stat-label {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.7);
  }
`

const ClassDisplayCard = styled.div`
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border-radius: 16px;
  padding: 1.25rem;
  border: 1px solid rgba(102, 126, 234, 0.2);
  transition: all 0.3s ease;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);

  &:hover {
    border-color: rgba(102, 126, 234, 0.4);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }

  .classes-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  .classes-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    color: #ffffff;
  }

  .classes-count {
    background: #800085;
    color: white;
    padding: 0.25rem 0.75rem;
    border-radius: 12px;
    font-size: 0.875rem;
    font-weight: 600;
  }

  .classes-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .class-tag {
    padding: 0.25rem 0.75rem;
    border-radius: 12px;
    font-size: 0.875rem;
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }
  }
`

const OverviewCard = styled.div`
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border-radius: 16px;
  padding: 1.25rem;
  border: 1px solid rgba(102, 126, 234, 0.2);
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
  transition: all 0.3s ease;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);

  &:hover {
    border-color: rgba(102, 126, 234, 0.4);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }
`

const OverviewItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 120px;
`

const OverviewLabel = styled.div`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
`

const OverviewValue = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #ffffff;
`

const ActionsSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
`

const ActionButton = styled.button<{
  $variant?: 'primary' | 'danger' | 'success' | 'selected' | 'warning'
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  cursor: pointer;
  border: none;
  min-width: 220px;

  ${(props) => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: #800085;
          color: white;
          &:hover:not(:disabled) {
            background: #6b006b;
          }
        `
      case 'danger':
        return `
          background: #ef4444;
          color: white;
          &:hover:not(:disabled) {
            background: #dc2626;
          }
        `
      case 'success':
        return `
          background: #10b981;
          color: white;
          &:hover:not(:disabled) {
            background: #059669;
          }
        `
      case 'warning':
        return `
          background: #f59e0b;
          color: white;
          &:hover:not(:disabled) {
            background: #d97706;
          }
        `
      case 'selected':
        return `
          background: #f59e0b;
          color: white;
          cursor: default;
        `
      default:
        return `
          background: rgba(255, 255, 255, 0.1);
          color: rgba(255, 255, 255, 0.8);
          border: 1px solid rgba(255, 255, 255, 0.2);
          &:hover:not(:disabled) {
            background: rgba(255, 255, 255, 0.15);
          }
        `
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const ActionZone = styled.div`
  background: transparent;
  border-radius: 12px;
  padding: 1.5rem;
  border: 2px solid #800085;
  height: 100%;
  display: flex;
  flex-direction: column;

  .zone-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #800085;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .zone-description {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.875rem;
    margin-bottom: 1rem;
    flex: 1;
  }
`

const TrainingZone = styled.div`
  background: transparent;
  border-radius: 12px;
  padding: 1.5rem;
  border: 2px solid #f59e0b;
  height: 100%;
  display: flex;
  flex-direction: column;

  .zone-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #f59e0b;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .zone-description {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.875rem;
    margin-bottom: 1rem;
    flex: 1;
  }
`

const DangerZone = styled.div`
  background: transparent;
  border-radius: 12px;
  padding: 1.5rem;
  border: 2px solid #ef4444;
  height: 100%;
  display: flex;
  flex-direction: column;

  .danger-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #ef4444;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .danger-description {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.875rem;
    margin-bottom: 1rem;
    flex: 1;
  }
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  flex-direction: column;
  gap: 1rem;
`

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  flex-direction: column;
  gap: 1rem;
  color: #ef4444;
`

type ModelDetailTab =
  | 'overview'
  | 'training-performance'
  | 'deployment'
  | 'content'
  | 'model-versions'
  | 'compare'
  | 'settings'

const ModelDetailView: React.FC = () => {
  const { projectId, modelId } = useParams<{ projectId: string; modelId: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const [activeTab, setActiveTab] = useState<ModelDetailTab>('overview')

  // Model name editing state
  const [isEditingName, setIsEditingName] = useState(false)
  const [editedName, setEditedName] = useState('')
  const [isUpdatingName, setIsUpdatingName] = useState(false)
  const [isPollingDeployment, setIsPollingDeployment] = useState(false)
  const [isPollingTraining, setIsPollingTraining] = useState(false)

  const {
    data: model,
    isLoading: modelLoading,
    error: modelError,
    refetch: refetchModel,
  } = useGetModelQuery(modelId || '', {
    skip: !modelId,
    // Poll every 2 seconds if deployment is in progress or training is stopping
    pollingInterval: isPollingDeployment || isPollingTraining ? 2000 : 0,
    // Let RTK Query handle automatic refetching via cache invalidation
  })

  // Monitor deployment status and stop polling when complete
  useEffect(() => {
    if (model?.deployment?.status === 'IN_SERVICE') {
      setIsPollingDeployment(false)
    }
  }, [model?.deployment?.status])

  // Monitor training status and stop polling when training is no longer active
  useEffect(() => {
    if (model?.state && model.state !== 'TRAINING' && model.state !== 'STOPPING') {
      setIsPollingTraining(false)
    } else if (model?.state === 'STOPPING' || model?.state === 'TRAINING') {
      // If model is in STOPPING or TRAINING state, start polling to detect when it completes
      setIsPollingTraining(true)
    }
  }, [model?.state])

  const { data: project, isLoading: projectLoading } = useGetProjectQuery(projectId || '', {
    skip: !projectId,
    refetchOnMountOrArgChange: false, // Use cached data if available
  })

  // Get all models to look up parent model names
  const { data: modelsData } = useFetchModelsQuery(
    { project_id: projectId || '' },
    {
      skip: !projectId,
    }
  )

  // Get tags data for class colors
  const { data: tagsData } = useGetTagsQuery(
    {
      project_id: projectId || '',
      dataset_id: project?.datasetid || '',
    },
    {
      skip: !projectId || !project?.datasetid,
      refetchOnMountOrArgChange: false,
    }
  )

  // Get hyperparameters separately for better data structure
  const { data: hyperparametersData } = useGetDefaultHyperparametersQuery(modelId || '', {
    skip: !modelId,
  })

  // Deploy/Delete functionality
  const [deployModel, { isLoading: isDeployingModel }] = useDeployModelMutation()
  const [deleteDeployment, { isLoading: isDeletingDeployment }] = useDeleteModelDeploymentMutation()
  const [deleteModel, { isLoading: isDeletingModel }] = useDeleteModelMutation()
  const [stopTrainModel, { isLoading: isStoppingTraining }] = useStopTrainModelMutation()
  const [updateModel] = useUpdateModelMutation()
  const [showDeleteDeploymentConfirm, setShowDeleteDeploymentConfirm] = useState<string | null>(
    null
  )
  const [showDeleteModelConfirm, setShowDeleteModelConfirm] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Get dataset information for the model
  const getDatasetInformation = useCallback(
    (model: any) => {
      if (!model || !project)
        return {
          training: [],
          validation: [],
          imageSize: undefined,
          epochs: undefined,
          augmentation: false,
        }

      // Get dataset sources from model parameters
      const training = model.parameters?.train_metadata?.datasource || []
      const validation = model.parameters?.validation_metadata?.datasource || []

      // Get hyperparameters - use dedicated API endpoint data if available
      const hp = hyperparametersData || model.parameters?.hyperparameters
      const inner = hp?.hyperparameters
      const epochs = (inner?.training?.epochs?.value as number) || undefined

      let imageSize: number | undefined = undefined
      let augmentation = false

      if (hp?.type === 'object_detection') {
        const odTraining = (inner as any)?.training
        imageSize = odTraining?.image_shape?.value as number | undefined
        augmentation = (odTraining?.augment?.value as boolean | undefined) ?? false
      } else if (hp?.type === 'classification') {
        const clTraining = (inner as any)?.training
        imageSize = clTraining?.image_shape?.value as number | undefined
        augmentation = (clTraining?.augment?.value as boolean | undefined) ?? false
      }

      return { training, validation, imageSize, epochs, augmentation }
    },
    [project]
  )

  const datasetInfo = useMemo(() => {
    if (!model)
      return {
        training: [],
        validation: [],
        imageSize: undefined,
        epochs: undefined,
        augmentation: false,
      }

    return getDatasetInformation(model)
  }, [model, getDatasetInformation])

  // Memoize datasource arrays to prevent unnecessary re-renders
  const trainingDatasources = useMemo(
    () => datasetInfo.training,
    [JSON.stringify(datasetInfo.training)]
  )
  const validationDatasources = useMemo(
    () => datasetInfo.validation,
    [JSON.stringify(datasetInfo.validation)]
  )

  // Get dataset stats for training and validation datasets (lazy-loaded to improve performance)
  const { data: trainStats } = useAnnotationStats(
    project?.id || '',
    project?.datasetid || '',
    trainingDatasources,
    {
      skip:
        !project?.id ||
        !project?.datasetid ||
        trainingDatasources.length === 0 ||
        activeTab !== 'overview',
      refetchOnMountOrArgChange: false, // Use cached data if available
    }
  )

  const { data: validationStats } = useAnnotationStats(
    project?.id || '',
    project?.datasetid || '',
    validationDatasources,
    {
      skip:
        !project?.id ||
        !project?.datasetid ||
        validationDatasources.length === 0 ||
        activeTab !== 'overview',
      refetchOnMountOrArgChange: false, // Use cached data if available
    }
  )

  // ModelDetailView handles its own model selection via URL params
  // No need to set global Redux state

  // Redirect to models page if no model ID
  useEffect(() => {
    if (!modelId) {
      navigate(`/models/${selectedProjectId}/overview`)
    }
  }, [modelId, selectedProjectId, navigate])

  const handleBackClick = () => {
    navigate(`/models/${selectedProjectId}/overview`)
  }

  // Model name editing handlers
  const handleStartEditName = () => {
    if (model) {
      setEditedName(model.name)
      setIsEditingName(true)
    }
  }

  const handleCancelEditName = () => {
    setIsEditingName(false)
    setEditedName('')
  }

  const handleSaveName = async () => {
    if (!model || !editedName.trim() || editedName === model.name) {
      setIsEditingName(false)
      return
    }

    setIsUpdatingName(true)
    try {
      const updateData: ModelUpdateParams = {
        name: editedName.trim(),
        description: model.description || '',
        parameters: model.parameters || ({} as any),
      }

      await updateModel({
        id: model.id,
        modelData: updateData,
      }).unwrap()

      setIsEditingName(false)
      setEditedName('')
    } catch (error: any) {
      setError(error?.data?.message || 'Failed to update model name')
    } finally {
      setIsUpdatingName(false)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'trained':
        return <CheckCircle size={16} />
      case 'training':
        return <Clock size={16} />
      case 'deployed':
        return <Rocket size={16} />
      case 'failed':
        return <XCircle size={16} />
      default:
        return <AlertCircle size={16} />
    }
  }

  // Helper functions
  const getDeploymentStatusForModel = useCallback((m: any) => {
    if (!m || !m.deployment) return 'not_deployed'
    const status = m.deployment.status
    if (status === 'IN_SERVICE') return 'deployed'
    if (status === 'CREATING' || status === 'INITIALIZED') return 'deploying'
    if (status === 'ERR') return 'failed'
    return 'not_deployed'
  }, [])

  const getModelStatus = useCallback((m: any) => {
    if (m.state === 'TRAINING') return 'training'
    if (m.state === 'TRAINED') return 'trained'
    if (m.state === 'ERR') return 'error'
    return 'unknown'
  }, [])

  // Helper function to get metric descriptions
  const getMetricDescription = (key: string): string => {
    const descriptions: { [key: string]: string } = {
      'calculated:f1': 'Harmonic mean of precision and recall',
      'validation:mAP50-95': 'Mean Average Precision averaged across IoU thresholds 0.5-0.95',
      'validation:precision': 'Ratio of true positive predictions to total positive predictions',
      'validation:recall': 'Ratio of true positive predictions to total actual positives',
    }
    return descriptions[key] || 'Performance metric'
  }

  const performanceMetrics = useMemo(() => {
    if (!model || !project) return []
    const isClassification = project?.annotation_type === 'classification'
    const metrics = model?.metrics || {}

    if (isClassification) {
      // For classification, use the same logic as Performance tab
      const trainAccuracy = metrics?.['train:accuracy']
      let valAccuracy = metrics?.['validation:accuracy']

      // Try to get the latest accuracy from training job entries
      const classificationStats = model?.training_stats?.classification
      if (classificationStats?.training_job_metrics) {
        const jobEntries = Object.values(classificationStats.training_job_metrics)[0]
          ?.training_job_entries
        if (jobEntries && jobEntries.length > 0) {
          const latestEntry = jobEntries[jobEntries.length - 1]
          if (latestEntry.accuracy !== null) {
            valAccuracy = latestEntry.accuracy
          }
        }
      }

      return [
        {
          label: 'Train Accuracy',
          value: trainAccuracy ? `${((trainAccuracy as number) * 100).toFixed(1)}%` : 'N/A',
          icon: TrendingUp,
          description: 'Final training accuracy achieved',
        },
        {
          label: 'Validation Accuracy',
          value: valAccuracy ? `${((valAccuracy as number) * 100).toFixed(1)}%` : 'N/A',
          icon: BarChart3,
          description: 'Final validation accuracy achieved',
        },
      ]
    }

    // For object detection, use the same logic as Performance tab
    const allowedMetrics = ['validation:mAP50-95', 'validation:precision', 'validation:recall']

    let recall = 0
    let precision = 0
    const validationMetrics = Object.entries(metrics)
      .filter(([key]) => allowedMetrics.includes(key) && key.startsWith('validation:'))
      .map(([key, value]) => {
        const percentageValue = typeof value === 'number' ? value * 100 : 0
        if (key.includes('precision')) {
          precision = typeof value === 'number' ? value * 100 : 0
        }
        if (key.includes('recall')) {
          recall = typeof value === 'number' ? value * 100 : 0
        }
        return { key, value: percentageValue }
      })

    const f1Score = (2 * (precision * recall)) / (precision + recall) || 0
    const combinedMetrics = [...validationMetrics, { key: 'calculated:f1', value: f1Score }]

    // Metric name mapping (same as Performance tab)
    const metricNameMap: { [key: string]: string } = {
      'calculated:f1': 'F1 Score',
      'validation:mAP50-95': 'mAP',
      'validation:precision': 'Precision',
      'validation:recall': 'Recall',
    }

    // Icon mapping
    const iconMap: { [key: string]: any } = {
      'calculated:f1': TrendingUp,
      'validation:mAP50-95': BarChart3,
      'validation:precision': ArrowUp,
      'validation:recall': ArrowDown,
    }

    return combinedMetrics.map(({ key, value }) => ({
      label: metricNameMap[key] || key,
      value: `${value.toFixed(1)}%`,
      icon: iconMap[key] || TrendingUp,
      description: getMetricDescription(key),
    }))
  }, [model, project])

  // Get class names and colors from annotation stats and tags
  const classInfo = useMemo(() => {
    if (!trainStats && !validationStats) {
      return { classNames: [] as string[], totalClasses: 0, classData: [] }
    }

    const tags = tagsData?.tags || []
    const tagIdToInfoMap = tags.reduce(
      (acc: Record<string, { name: string; color: string }>, tag: any) => {
        acc[tag.id] = {
          name: tag.name,
          color: `#${tag.color.toString(16).padStart(6, '0').slice(0, 6)}`,
        }
        return acc
      },
      {} as Record<string, { name: string; color: string }>
    )

    const toClassData = (perClass?: Record<string, { name?: string | null; count?: number }>) => {
      if (!perClass) return []

      return Object.entries(perClass)
        .map(([tagId, classData]) => {
          const tagInfo = tagIdToInfoMap[tagId]
          const fallbackColor = '#800085' // Default purple color

          return {
            id: tagId,
            name: tagInfo?.name || classData.name || 'Unknown',
            color: tagInfo?.color || fallbackColor,
            count: classData.count || 0,
          }
        })
        .filter((cls) => cls.name && cls.name !== 'Unknown' && cls.name !== 'null')
    }

    const trainClassData = toClassData(
      trainStats?.statistics?.annotations_per_class as Record<
        string,
        { name?: string | null; count?: number }
      >
    )
    const validationClassData = toClassData(
      validationStats?.statistics?.annotations_per_class as Record<
        string,
        { name?: string | null; count?: number }
      >
    )

    // Combine and deduplicate class data
    const allClassData = [...trainClassData, ...validationClassData]
    const uniqueClassData = allClassData.reduce((acc, current) => {
      const existing = acc.find((item) => item.name === current.name)
      if (!existing) {
        acc.push(current)
      } else {
        // Use the one with higher count or better color info
        if (
          current.count > existing.count ||
          (current.color !== '#800085' && existing.color === '#800085')
        ) {
          const index = acc.findIndex((item) => item.name === current.name)
          acc[index] = current
        }
      }
      return acc
    }, [] as Array<{ id: string; name: string; color: string; count: number }>)

    return {
      classNames: uniqueClassData.map((cls) => cls.name),
      totalClasses: uniqueClassData.length,
      classData: uniqueClassData,
    }
  }, [trainStats, validationStats, tagsData])

  const fullDatasetInfo = useMemo(() => {
    const trainImageCount =
      (trainStats?.statistics?.total_annotated_images || 0) +
      (trainStats?.statistics?.total_unannotated_images || 0)
    const validationImageCount =
      (validationStats?.statistics?.total_annotated_images || 0) +
      (validationStats?.statistics?.total_unannotated_images || 0)

    return {
      ...datasetInfo,
      trainImageCount,
      validationImageCount,
      trainAnnotations: trainStats?.statistics?.total_annotations || 0,
      validationAnnotations: validationStats?.statistics?.total_annotations || 0,
    }
  }, [datasetInfo, trainStats, validationStats])

  // Action handlers
  const handleDeploy = useCallback(async () => {
    if (!model) return
    try {
      await deployModel({ id: model.id }).unwrap()
      // Start polling to check deployment status
      setIsPollingDeployment(true)
    } catch (error: unknown) {
      const message = (error as { data?: { message?: string } })?.data?.message
      setError(message || 'Failed to deploy model')
      setIsPollingDeployment(false)
    }
  }, [deployModel, model])

  const handleDeleteDeployment = useCallback(async () => {
    if (!model) return
    try {
      await deleteDeployment({ id: model.id }).unwrap()
      setShowDeleteDeploymentConfirm(null)
      setIsPollingDeployment(false)
    } catch (error: unknown) {
      const message = (error as { data?: { message?: string } })?.data?.message
      setError(message || 'Failed to remove deployment')
    }
  }, [deleteDeployment, model])

  const handleDeleteModel = useCallback(async () => {
    if (!model) return
    try {
      // Navigate first to avoid any transient 404s on this page causing auth issues
      navigate(`/models/${selectedProjectId}/overview`, { replace: true })
      // Fire-and-forget the deletion; errors will not block navigation
      deleteModel({ id: model.id })
        .unwrap()
        .catch((err) => {
          console.error('Failed to delete model:', err)
        })
      setShowDeleteModelConfirm(null)
    } catch (error: unknown) {
      const message = (error as { data?: { message?: string } })?.data?.message
      setError(message || 'Failed to delete model')
    }
  }, [deleteModel, model, navigate, selectedProjectId])

  const handleStopTraining = useCallback(async () => {
    if (!model) return
    try {
      await stopTrainModel({ id: model.id }).unwrap()
      // Start polling to check training status
      setIsPollingTraining(true)
      refetchModel()
    } catch (error: unknown) {
      const message = (error as { data?: { message?: string } })?.data?.message
      setError(message || 'Failed to stop training')
      setIsPollingTraining(false)
    }
  }, [stopTrainModel, model, refetchModel])

  // Helper function to format hyperparameter values - always returns a string
  const formatHyperparameterValue = (value: any, depth = 0): string => {
    // Prevent infinite recursion
    if (depth > 5) {
      console.warn('formatHyperparameterValue: Max depth exceeded', value)
      return '[Complex Object]'
    }
    // Handle null or undefined
    if (value === null || value === undefined) {
      return ''
    }

    // Handle primitives first
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No'
    }

    if (typeof value === 'string' || typeof value === 'number') {
      return String(value)
    }

    // Handle arrays - format each element recursively
    if (Array.isArray(value)) {
      return value
        .map((v) => {
          const formatted = formatHyperparameterValue(v, depth + 1)
          return formatted || '[array element]'
        })
        .join(', ')
    }

    // Handle objects
    if (typeof value === 'object') {
      // Check if this looks like a hyperparameter object (has name, value, and either type or input_type)
      if ('name' in value && 'value' in value && ('type' in value || 'input_type' in value)) {
        const val = value.value
        // If the nested value is a primitive, use it
        if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
          return typeof val === 'boolean' ? (val ? 'Yes' : 'No') : String(val)
        }
        // If the nested value is still an object, try to extract from it recursively
        if (typeof val === 'object' && val !== null) {
          // Recursively format the nested value
          const nested = formatHyperparameterValue(val, depth + 1)
          if (nested && nested !== '[Complex Object]') return nested
        }
        // Otherwise, show the type or a default message
        const typeDisplay = value.type || value.input_type || 'unknown'
        return `[${typeDisplay}]`
      }

      // If it has a 'value' property, try to use it
      if ('value' in value && typeof value.value !== 'undefined') {
        const val = value.value
        // If it's a primitive, use it directly
        if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
          return typeof val === 'boolean' ? (val ? 'Yes' : 'No') : String(val)
        }
        // If it's still an object, recursively format it
        if (typeof val === 'object' && val !== null) {
          const nested = formatHyperparameterValue(val, depth + 1)
          if (nested && nested !== '[Complex Object]' && nested.length > 0) return nested
        }
      }

      // If it has min/max properties, show as range
      if ('min_value' in value && 'max_value' in value) {
        return `${value.min_value} - ${value.max_value}`
      }

      // If it has enabled property, show as Enabled/Disabled
      if ('enabled' in value) {
        return value.enabled ? 'Enabled' : 'Disabled'
      }

      // For other objects, show as JSON to avoid rendering issues
      try {
        const jsonStr = JSON.stringify(value)
        // Ensure we return a string, never return undefined/null
        return (jsonStr || '{}').substring(0, 100) // Truncate if too long
      } catch (e) {
        console.error('Failed to stringify object:', value, e)
        return '[Unparseable Object]'
      }
    }

    // For any other type, return a safe string representation
    try {
      const result = String(value)
      // Check if String() returned the object representation
      if (result === '[object Object]' || result === '') {
        return '[Unknown]'
      }
      return result
    } catch (e) {
      console.error('Failed to convert value to string:', value, e)
      return '[Error]'
    }
  }

  // Wrap the formatter to ensure it ALWAYS returns a string
  const safeFormatHyperparameterValue = (value: any): string => {
    // Handle null/undefined
    if (value === null || value === undefined) {
      return ''
    }

    // Handle strings
    if (typeof value === 'string') {
      return value
    }

    // Handle booleans
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No'
    }

    // Handle numbers
    if (typeof value === 'number') {
      return String(value)
    }

    // Handle non-object primitives
    if (typeof value !== 'object') {
      return String(value)
    }

    // Handle arrays
    if (Array.isArray(value)) {
      try {
        return value.map((v) => safeFormatHyperparameterValue(v)).join(', ')
      } catch (e) {
        return '[Array]'
      }
    }

    // Handle objects - must be a hyperparameter object
    // Hyperparameter objects have structure: {name, type, input_type, value, min_value, max_value}

    // First check: does it have the hyperparameter structure?
    if ('name' in value && 'type' in value && 'value' in value) {
      // It's a hyperparameter object, extract the value
      const actualValue = value.value

      // If the value is a primitive, return it
      if (typeof actualValue === 'string' || typeof actualValue === 'number') {
        return String(actualValue)
      }

      if (typeof actualValue === 'boolean') {
        return actualValue ? 'Yes' : 'No'
      }

      // If value is also an object, recursively handle it
      if (typeof actualValue === 'object' && actualValue !== null) {
        return safeFormatHyperparameterValue(actualValue)
      }

      // If value is undefined, try to show the type
      return `[${value.type || 'unknown'}]`
    }

    // Second check: just has a 'value' property
    if ('value' in value && value.value !== undefined) {
      const actualValue = value.value

      // If the value is a primitive, return it
      if (typeof actualValue === 'string' || typeof actualValue === 'number') {
        return String(actualValue)
      }

      if (typeof actualValue === 'boolean') {
        return actualValue ? 'Yes' : 'No'
      }

      // If value is also an object, recursively handle it
      if (typeof actualValue === 'object' && actualValue !== null) {
        return safeFormatHyperparameterValue(actualValue)
      }
    }

    // If it has min_value and max_value, show as range
    if ('min_value' in value && 'max_value' in value) {
      return `${value.min_value} - ${value.max_value}`
    }

    // Last resort: stringify the object (truncated)
    try {
      const str = JSON.stringify(value)
      return str.length > 100 ? str.substring(0, 97) + '...' : str
    } catch (e) {
      return '[Object]'
    }

    // Ultimate fallback - should never reach here, but ensures we always return a string
    return '[Unknown Value]'
  }

  const renderSettingsContent = () => {
    if (!model || !project) {
      return (
        <Card
          className="h-100"
          style={{ background: 'transparent', border: '1px solid rgba(64, 64, 64, 0.3)' }}
        >
          <Card.Header
            style={{ background: 'transparent', borderBottom: '1px solid rgba(64, 64, 64, 0.3)' }}
          >
            <Card.Title className="text-white mb-0">Model Settings</Card.Title>
          </Card.Header>
          <Card.Body className="d-flex align-items-center justify-content-center">
            <div className="text-center">
              <Settings size={64} className="text-light mb-3" style={{ color: '#e9ecef' }} />
              <h5 className="text-white mb-3">No Model Selected</h5>
              <p className="text-light" style={{ color: '#e9ecef' }}>
                Please select a model to view its settings and hyperparameters.
              </p>
            </div>
          </Card.Body>
        </Card>
      )
    }

    // Use hyperparameters from the dedicated API endpoint if available, fallback to model parameters
    const hyperparameters = hyperparametersData || model.parameters?.hyperparameters
    const inner = hyperparameters?.hyperparameters

    return (
      <Container fluid>
        <Row className="g-4">
          {/* Left Panel - Model Information */}
          <Col lg={4}>
            <Card
              className="h-100"
              style={{ background: 'transparent', border: '1px solid rgba(64, 64, 64, 0.3)' }}
            >
              <Card.Header
                style={{
                  background: 'transparent',
                  borderBottom: '1px solid rgba(64, 64, 64, 0.3)',
                }}
              >
                <Card.Title className="text-white mb-0">Model Information</Card.Title>
              </Card.Header>
              <Card.Body className="d-flex flex-column">
                <div className="mb-4">
                  <h6 className="text-white mb-3">Model Details</h6>
                  <div className="mb-3">
                    <strong className="text-white">Name:</strong>
                    <span className="text-light ms-2" style={{ color: '#e9ecef' }}>
                      {model.name || 'Unknown Model'}
                    </span>
                  </div>
                  <div className="mb-3">
                    <strong className="text-white">Status:</strong>
                    <Badge
                      bg={
                        model.state === 'TRAINED'
                          ? 'success'
                          : model.state === 'TRAINING'
                          ? 'warning'
                          : 'secondary'
                      }
                      className="ms-2"
                    >
                      {model.state || 'Unknown'}
                    </Badge>
                  </div>
                  <div className="mb-3">
                    <strong className="text-white">Created:</strong>
                    <span className="text-light ms-2" style={{ color: '#e9ecef' }}>
                      {model.created_at
                        ? new Date(model.created_at).toLocaleDateString()
                        : 'Unknown'}
                    </span>
                  </div>
                  {model.description && (
                    <div className="mb-3">
                      <strong className="text-white">Description:</strong>
                      <p className="text-light mt-1" style={{ color: '#e9ecef' }}>
                        {model.description}
                      </p>
                    </div>
                  )}
                </div>

                <div className="mb-4">
                  <h6 className="text-white mb-3">Training Information</h6>
                  <div
                    className="p-3"
                    style={{
                      background: 'transparent',
                      borderRadius: '6px',
                      border: '1px solid #404040',
                    }}
                  >
                    <div className="mb-2">
                      <span className="text-light" style={{ color: '#e9ecef' }}>
                        Model Type:
                      </span>
                      <span className="text-white fw-bold ms-2">
                        {hyperparameters?.type || 'N/A'}
                      </span>
                    </div>
                    <div className="mb-2">
                      <span className="text-light" style={{ color: '#e9ecef' }}>
                        Has Hyperparameters:
                      </span>
                      <span className="text-white fw-bold ms-2">
                        {hyperparameters?.hyperparameters ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="mb-2">
                      <span className="text-light" style={{ color: '#e9ecef' }}>
                        Training Status:
                      </span>
                      <span className="text-white fw-bold ms-2">{model.state || 'Unknown'}</span>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <h6 className="text-white mb-3">Model Information</h6>
                  <div
                    className="p-3"
                    style={{
                      background: 'transparent',
                      borderRadius: '6px',
                      border: '1px solid #404040',
                    }}
                  >
                    <div className="mb-3">
                      <strong className="text-white">Model Name:</strong>
                      <div className="text-light mt-1" style={{ color: '#e9ecef' }}>
                        {model.name || 'Unnamed Model'}
                      </div>
                    </div>
                    <div className="mb-3">
                      <strong className="text-white">Architecture:</strong>
                      <div className="text-light mt-1" style={{ color: '#e9ecef' }}>
                        {hyperparameters?.type?.replace(/_/g, ' ').toUpperCase() || 'Unknown'}
                      </div>
                    </div>
                    <div className="mb-3">
                      <strong className="text-white">Base Model:</strong>
                      <div className="text-light mt-1" style={{ color: '#e9ecef' }}>
                        {model.parent_id && modelsData?.models
                          ? modelsData.models.find((m) => m.id === model.parent_id)?.name ||
                            `Model ${model.parent_id}`
                          : 'Root Model'}
                      </div>
                    </div>
                    <div className="mb-3">
                      <strong className="text-white">Created:</strong>
                      <div className="text-light mt-1" style={{ color: '#e9ecef' }}>
                        {model.created_at
                          ? new Date(model.created_at).toLocaleDateString()
                          : 'Unknown'}
                      </div>
                    </div>
                    {model.description && (
                      <div className="mb-3">
                        <strong className="text-white">Description:</strong>
                        <div className="text-light mt-1" style={{ color: '#e9ecef' }}>
                          {model.description}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mb-4">
                  <h6 className="text-white mb-3">Training Information</h6>
                  <div
                    className="p-3"
                    style={{
                      background: 'transparent',
                      borderRadius: '6px',
                      border: '1px solid #404040',
                    }}
                  >
                    <div className="mb-3">
                      <strong className="text-white">Dataset:</strong>
                      <div className="text-light mt-1" style={{ color: '#e9ecef' }}>
                        {model.datasetid ? `Dataset ${model.datasetid}` : 'No Dataset'}
                      </div>
                    </div>
                    <div className="mb-3">
                      <strong className="text-white">Training Status:</strong>
                      <div className="text-light mt-1" style={{ color: '#e9ecef' }}>
                        {model.state === 'TRAINED'
                          ? 'Completed'
                          : model.state === 'TRAINING'
                          ? 'In Progress'
                          : model.state === 'INITIALIZED'
                          ? 'Ready to Train'
                          : model.state === 'ERR'
                          ? 'Failed'
                          : model.state}
                      </div>
                    </div>
                    {model.train_started_at && (
                      <div className="mb-3">
                        <strong className="text-white">Training Started:</strong>
                        <div className="text-light mt-1" style={{ color: '#e9ecef' }}>
                          {new Date(model.train_started_at).toLocaleDateString()}
                        </div>
                      </div>
                    )}
                    {model.train_ended_at && (
                      <div className="mb-3">
                        <strong className="text-white">Training Completed:</strong>
                        <div className="text-light mt-1" style={{ color: '#e9ecef' }}>
                          {new Date(model.train_ended_at).toLocaleDateString()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-auto">
                  <div
                    className="p-3"
                    style={{
                      background: 'transparent',
                      borderRadius: '6px',
                      border: '1px solid #404040',
                    }}
                  >
                    <h6 className="text-white mb-2">Status</h6>
                    <div className="row g-2">
                      <div className="col-6">
                        <div className="text-center">
                          <div className="text-white fw-bold">{model.datasetid ? 'Yes' : 'No'}</div>
                          <small className="text-light" style={{ color: '#e9ecef' }}>
                            Has Dataset
                          </small>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="text-center">
                          <div className="text-white fw-bold">
                            {model.deployment ? 'Yes' : 'No'}
                          </div>
                          <small className="text-light" style={{ color: '#e9ecef' }}>
                            Deployed
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Right Panel - Hyperparameters */}
          <Col lg={8}>
            <Card
              className="h-100"
              style={{ background: 'transparent', border: '1px solid rgba(64, 64, 64, 0.3)' }}
            >
              <Card.Header
                style={{
                  background: 'transparent',
                  borderBottom: '1px solid rgba(64, 64, 64, 0.3)',
                }}
              >
                <Card.Title className="text-white mb-0">Training Hyperparameters</Card.Title>
              </Card.Header>
              <Card.Body className="d-flex flex-column">
                {hyperparameters ? (
                  <div className="flex-grow-1">
                    {/* Model Type Summary */}
                    <div className="mb-4">
                      <div
                        className="p-4"
                        style={{
                          background: 'transparent',
                          borderRadius: '8px',
                          border: '1px solid #404040',
                        }}
                      >
                        <div className="row align-items-center">
                          <div className="col-md-8">
                            <h5 className="text-white mb-2">Model Configuration</h5>
                            <p className="text-light mb-0" style={{ color: '#e9ecef' }}>
                              {hyperparameters.type?.replace(/_/g, ' ').toUpperCase() ||
                                'Unknown Model Type'}
                            </p>
                          </div>
                          <div className="col-md-4 text-end">
                            <Badge
                              bg="success"
                              style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
                            >
                              Configuration Available
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Training Parameters - Card Grid */}
                    {inner?.training && (
                      <div className="mb-4">
                        <h5 className="text-white mb-3">Training Settings</h5>
                        <div className="row g-3">
                          {Object.entries(inner.training).map(([key, value]: [string, any]) => (
                            <div key={key} className="col-md-6 col-lg-4">
                              <div
                                className="p-3"
                                style={{
                                  background: 'transparent',
                                  borderRadius: '8px',
                                  border: '1px solid #404040',
                                  height: '100%',
                                }}
                              >
                                <div className="text-center">
                                  <div
                                    className="text-white fw-bold mb-1"
                                    style={{ fontSize: '1.1rem' }}
                                  >
                                    {String(safeFormatHyperparameterValue(value))}
                                  </div>
                                  <div
                                    className="text-light"
                                    style={{ color: '#e9ecef', fontSize: '0.85rem' }}
                                  >
                                    {key
                                      .replace(/_/g, ' ')
                                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Data Augmentation - Organized by Category */}
                    {inner?.transforms && (
                      <div className="mb-4">
                        <h5 className="text-white mb-3">Data Augmentation</h5>
                        <div className="row g-3">
                          {Object.entries(inner.transforms).map(
                            ([transformName, transformParams]: [string, any]) => (
                              <div key={transformName} className="col-md-6 col-lg-4">
                                <div
                                  className="p-3"
                                  style={{
                                    background: 'transparent',
                                    borderRadius: '8px',
                                    border: '1px solid #404040',
                                    height: '100%',
                                  }}
                                >
                                  <div className="d-flex align-items-center mb-3">
                                    <div
                                      className="me-2"
                                      style={{
                                        width: '10px',
                                        height: '10px',
                                        borderRadius: '50%',
                                        backgroundColor: '#0d6efd',
                                      }}
                                    ></div>
                                    <h6 className="text-white mb-0" style={{ fontSize: '0.95rem' }}>
                                      {transformName
                                        .replace(/_/g, ' ')
                                        .replace(/\b\w/g, (l) => l.toUpperCase())}
                                    </h6>
                                  </div>
                                  <div className="space-y-2">
                                    {/* Only render the 'value' property from hyperparameter objects */}
                                    {(() => {
                                      // Check if transformParams is a hyperparameter object (has name, type, value properties)
                                      if (
                                        transformParams &&
                                        typeof transformParams === 'object' &&
                                        'name' in transformParams &&
                                        'type' in transformParams &&
                                        'value' in transformParams
                                      ) {
                                        const formattedValue = safeFormatHyperparameterValue(
                                          transformParams.value
                                        )
                                        return (
                                          <div className="d-flex justify-content-between align-items-center py-1">
                                            <span
                                              className="text-light"
                                              style={{ color: '#e9ecef', fontSize: '0.8rem' }}
                                            >
                                              Value
                                            </span>
                                            <Badge bg="secondary" style={{ fontSize: '0.75rem' }}>
                                              {String(formattedValue)}
                                            </Badge>
                                          </div>
                                        )
                                      }

                                      // Otherwise, render all entries (for non-hyperparameter objects)
                                      if (transformParams && typeof transformParams === 'object') {
                                        return Object.entries(transformParams)
                                          .map(([paramName, paramValue]: [string, any]) => {
                                            // Skip rendering ALL hyperparameter metadata keys
                                            if (
                                              paramName === 'name' ||
                                              paramName === 'type' ||
                                              paramName === 'input_type' ||
                                              paramName === 'allowed_values' ||
                                              paramName === 'min_value' ||
                                              paramName === 'max_value'
                                            ) {
                                              return null
                                            }

                                            // Check if paramValue itself is a hyperparameter object
                                            if (
                                              paramValue &&
                                              typeof paramValue === 'object' &&
                                              'name' in paramValue &&
                                              'type' in paramValue &&
                                              'value' in paramValue
                                            ) {
                                              return null // Skip hyperparameter objects entirely
                                            }

                                            const formattedValue =
                                              safeFormatHyperparameterValue(paramValue)
                                            return (
                                              <div
                                                key={paramName}
                                                className="d-flex justify-content-between align-items-center py-1"
                                              >
                                                <span
                                                  className="text-light"
                                                  style={{ color: '#e9ecef', fontSize: '0.8rem' }}
                                                >
                                                  {paramName
                                                    .replace(/_/g, ' ')
                                                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                                                </span>
                                                <Badge
                                                  bg="secondary"
                                                  style={{ fontSize: '0.75rem' }}
                                                >
                                                  {String(formattedValue)}
                                                </Badge>
                                              </div>
                                            )
                                          })
                                          .filter(Boolean) // Remove any null entries
                                      }

                                      return null
                                    })()}
                                  </div>
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}

                    {/* Other Parameters */}
                    {inner &&
                      Object.keys(inner).filter((key) => key !== 'training' && key !== 'transforms')
                        .length > 0 && (
                        <div className="mb-4">
                          <h5 className="text-white mb-3">Additional Settings</h5>
                          <div className="row g-3">
                            {Object.entries(inner)
                              .filter(([key]) => key !== 'training' && key !== 'transforms')
                              .map(([key, value]: [string, any]) => (
                                <div key={key} className="col-md-6 col-lg-4">
                                  <div
                                    className="p-3"
                                    style={{
                                      background: 'transparent',
                                      borderRadius: '8px',
                                      border: '1px solid #404040',
                                      height: '100%',
                                    }}
                                  >
                                    <div className="text-center">
                                      <div
                                        className="text-white fw-bold mb-1"
                                        style={{ fontSize: '1.1rem' }}
                                      >
                                        {String(safeFormatHyperparameterValue(value))}
                                      </div>
                                      <div
                                        className="text-light"
                                        style={{ color: '#e9ecef', fontSize: '0.85rem' }}
                                      >
                                        {key
                                          .replace(/_/g, ' ')
                                          .replace(/\b\w/g, (l) => l.toUpperCase())}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                  </div>
                ) : (
                  <div className="d-flex align-items-center justify-content-center flex-grow-1">
                    <div className="text-center">
                      <Settings
                        size={64}
                        className="text-light mb-3"
                        style={{ color: '#e9ecef' }}
                      />
                      <h5 className="text-white mb-3">No Hyperparameters Available</h5>
                      <p className="text-light" style={{ color: '#e9ecef' }}>
                        This model doesn't have hyperparameter information available.
                      </p>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    )
  }

  const renderOverviewContent = () => {
    if (!model || !project) return null

    const deploymentStatus = getDeploymentStatusForModel(model)
    const isDeployed = deploymentStatus === 'deployed'
    const isDeploying = deploymentStatus === 'deploying' || isDeployingModel

    // Extract hyperparameters for easy access
    // Use hyperparameters from the dedicated API endpoint if available, fallback to model parameters
    const hyperparameters = hyperparametersData || model.parameters?.hyperparameters
    const inner = hyperparameters?.hyperparameters
    const trainingParams = inner?.training

    return (
      <OverviewContainer>
        {/* Quick Stats Row */}
        <Section>
          <Row className="g-3">
            <Col md={4} sm={6}>
              <div
                style={{
                  background: 'transparent',
                  padding: '1rem',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  height: '100%',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.5rem',
                  }}
                >
                  <Clock size={18} color="#800085" />
                  <OverviewLabel style={{ marginBottom: 0 }}>Created</OverviewLabel>
                </div>
                <OverviewValue>{new Date(model.created_at).toLocaleDateString()}</OverviewValue>
              </div>
            </Col>
            <Col md={4} sm={6}>
              <div
                style={{
                  background: 'transparent',
                  padding: '1rem',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  height: '100%',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.5rem',
                  }}
                >
                  <Database size={18} color="#800085" />
                  <OverviewLabel style={{ marginBottom: 0 }}>Detection Classes</OverviewLabel>
                </div>
                <OverviewValue>
                  {classInfo.totalClasses} {classInfo.totalClasses === 1 ? 'class' : 'classes'}
                </OverviewValue>
              </div>
            </Col>
            <Col md={4} sm={6}>
              <div
                style={{
                  background: 'transparent',
                  padding: '1rem',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  height: '100%',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.5rem',
                  }}
                >
                  <TrendingUp size={18} color="#800085" />
                  <OverviewLabel style={{ marginBottom: 0 }}>Status</OverviewLabel>
                </div>
                <div>
                  {model.state === 'TRAINED' && (
                    <Badge bg="success" style={{ fontWeight: '600' }}>
                      Trained
                    </Badge>
                  )}
                  {model.state === 'TRAINING' && (
                    <Badge bg="warning" style={{ fontWeight: '600' }}>
                      Training
                    </Badge>
                  )}
                  {model.state === 'INITIALIZED' && (
                    <Badge bg="secondary" style={{ fontWeight: '600' }}>
                      Initialized
                    </Badge>
                  )}
                  {model.state === 'ERR' && (
                    <Badge bg="danger" style={{ fontWeight: '600' }}>
                      Error
                    </Badge>
                  )}
                  {isDeployed && (
                    <Badge bg="info" style={{ fontWeight: '600', marginLeft: '0.5rem' }}>
                      Deployed
                    </Badge>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Section>

        {/* Quick Actions */}
        <Section>
          <SectionTitle>
            <Settings size={18} />
            Quick Actions
          </SectionTitle>
          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'stretch', flexWrap: 'wrap' }}>
            {/* Training Zone */}
            {(model.state === 'TRAINING' || model.state === 'STOPPING') && (
              <div style={{ flex: 1, minWidth: '300px' }}>
                <TrainingZone>
                  <div className="zone-header">
                    <Zap size={16} />
                    Training Actions
                  </div>
                  <div className="zone-description">
                    Stop the training process for this model. Training progress will be saved up to
                    the current checkpoint.
                  </div>
                  <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'center' }}>
                    <ActionButton
                      $variant="warning"
                      onClick={handleStopTraining}
                      disabled={isStoppingTraining || model.state === 'STOPPING'}
                    >
                      <Square size={16} />
                      {isStoppingTraining
                        ? 'Stopping...'
                        : model.state === 'STOPPING'
                        ? 'Stopping...'
                        : 'Stop Training'}
                    </ActionButton>
                  </div>
                </TrainingZone>
              </div>
            )}

            {/* Initialized Model Zone - Show for all INITIALIZED models */}
            {model.state === 'INITIALIZED' && (
              <div style={{ flex: 1, minWidth: '300px' }}>
                <TrainingZone>
                  <div className="zone-header">
                    <Settings size={16} />
                    Model Setup
                  </div>
                  <div className="zone-description">
                    {datasetInfo.training.length > 0 || datasetInfo.validation.length > 0
                      ? 'This model is configured and ready to begin training. Continue to the training page to start training.'
                      : 'This model needs to be configured. Continue to the training page to set up datasets and start training.'}
                  </div>
                  <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'center' }}>
                    <ActionButton
                      $variant="primary"
                      onClick={() => navigate(`/training/${projectId}?modelId=${model.id}`)}
                    >
                      <Settings size={16} />
                      Continue Training Setup
                    </ActionButton>
                  </div>
                </TrainingZone>
              </div>
            )}

            {/* Deploy Zone */}
            {model.state === 'TRAINED' && (
              <div style={{ flex: 1, minWidth: '300px' }}>
                <ActionZone>
                  <div className="zone-header">
                    <Rocket size={16} />
                    Model Deployment
                  </div>
                  <div className="zone-description">
                    {isDeployed
                      ? 'This model is currently deployed and serving predictions.'
                      : 'Deploy this model to start using it for predictions.'}
                  </div>
                  <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'center' }}>
                    <ActionButton
                      $variant={isDeployed ? 'success' : 'primary'}
                      onClick={
                        isDeployed ? () => setShowDeleteDeploymentConfirm(model.id) : handleDeploy
                      }
                      disabled={isDeploying}
                    >
                      <Rocket
                        size={16}
                        style={{ transform: isDeployed ? 'rotate(180deg)' : 'none' }}
                      />
                      {isDeployed ? 'Undeploy' : isDeploying ? 'Deploying...' : 'Deploy Model'}
                    </ActionButton>
                  </div>
                </ActionZone>
              </div>
            )}

            {/* Danger Zone */}
            <div style={{ flex: 1, minWidth: '300px' }}>
              <DangerZone>
                <div className="danger-header">
                  <AlertCircle size={16} />
                  Danger Zone
                </div>
                <div className="danger-description">
                  Permanently delete this model. This action cannot be undone and will remove all
                  model data.
                </div>
                <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'center' }}>
                  <ActionButton
                    $variant="danger"
                    onClick={() => setShowDeleteModelConfirm(model.id)}
                    disabled={isDeletingModel}
                  >
                    <Trash2 size={16} />
                    {isDeletingModel ? 'Deleting...' : 'Delete Model Permanently'}
                  </ActionButton>
                </div>
              </DangerZone>
            </div>
          </div>
        </Section>

        {/* Performance Metrics */}
        {model.state === 'TRAINED' && performanceMetrics.length > 0 && (
          <Section>
            <SectionTitle>
              <TrendingUp size={18} />
              Performance Metrics
            </SectionTitle>
            <SectionContent>
              <MetricsGrid>
                {performanceMetrics.map((metric, index) => (
                  <MetricCard key={index} title={metric.description}>
                    <div className="metric-icon">
                      <metric.icon size={16} />
                    </div>
                    <div className="metric-value">{metric.value}</div>
                    <div className="metric-label">{metric.label}</div>
                  </MetricCard>
                ))}
              </MetricsGrid>
            </SectionContent>
          </Section>
        )}

        {/* Model Configuration */}
        <Section>
          <SectionTitle>
            <Settings size={18} />
            Model Configuration
          </SectionTitle>
          <SectionContent>
            <Row className="g-3">
              <Col md={6}>
                <div
                  style={{
                    background: 'transparent',
                    padding: '1rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    height: '100%',
                  }}
                >
                  <OverviewLabel>Model Type</OverviewLabel>
                  <OverviewValue>
                    {hyperparameters?.type?.replace(/_/g, ' ').toUpperCase() || 'N/A'}
                  </OverviewValue>
                </div>
              </Col>
              <Col md={6}>
                <div
                  style={{
                    background: 'transparent',
                    padding: '1rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    height: '100%',
                  }}
                >
                  <OverviewLabel>Input Size</OverviewLabel>
                  <OverviewValue>
                    {fullDatasetInfo.imageSize ? `${fullDatasetInfo.imageSize}px` : 'N/A'}
                  </OverviewValue>
                </div>
              </Col>
              <Col md={6}>
                <div
                  style={{
                    background: 'transparent',
                    padding: '1rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    height: '100%',
                  }}
                >
                  <OverviewLabel>Epochs Trained</OverviewLabel>
                  <OverviewValue>
                    {fullDatasetInfo.epochs ||
                      (hyperparameters?.hyperparameters as any)?.training?.epochs?.value ||
                      'N/A'}
                  </OverviewValue>
                </div>
              </Col>
              {trainingParams &&
                ((trainingParams as any)?.learning_rate || (trainingParams as any)?.lr) && (
                  <Col md={6}>
                    <div
                      style={{
                        background: 'transparent',
                        padding: '1rem',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        height: '100%',
                      }}
                    >
                      <OverviewLabel>Learning Rate</OverviewLabel>
                      <OverviewValue>
                        {(trainingParams as any)?.learning_rate ||
                          (trainingParams as any)?.lr ||
                          (trainingParams as any)?.['learning-rate'] ||
                          'N/A'}
                      </OverviewValue>
                    </div>
                  </Col>
                )}
              {trainingParams && (trainingParams as any)?.batch_size && (
                <Col md={6}>
                  <div
                    style={{
                      background: 'transparent',
                      padding: '1rem',
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      height: '100%',
                    }}
                  >
                    <OverviewLabel>Batch Size</OverviewLabel>
                    <OverviewValue>
                      {(trainingParams as any)?.batch_size ||
                        (trainingParams as any)?.['batch-size'] ||
                        'N/A'}
                    </OverviewValue>
                  </div>
                </Col>
              )}
              <Col md={6}>
                <div
                  style={{
                    background: 'transparent',
                    padding: '1rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    height: '100%',
                  }}
                >
                  <OverviewLabel>Training Duration</OverviewLabel>
                  <OverviewValue>
                    {model.train_started_at &&
                    model.train_ended_at &&
                    model.train_ended_at !== '0001-01-01T00:00:00Z'
                      ? formatDuration(
                          new Date(model.train_ended_at).getTime() -
                            new Date(model.train_started_at).getTime()
                        )
                      : 'N/A'}
                  </OverviewValue>
                </div>
              </Col>
            </Row>
          </SectionContent>
        </Section>

        {/* Detection Classes */}
        {classInfo.classNames.length > 0 && (
          <Section>
            <SectionTitle>
              <Eye size={18} />
              What This Model Detects
            </SectionTitle>
            <SectionContent>
              <ClassDisplayCard>
                <div className="classes-header">
                  <div className="classes-title">
                    <Package size={16} />
                    Detection Classes
                  </div>
                  <div className="classes-count">{classInfo.totalClasses}</div>
                </div>
                <div className="classes-grid">
                  {classInfo.classData.map((classItem, index) => (
                    <div
                      key={index}
                      className="class-tag"
                      style={{
                        backgroundColor: `${classItem.color}20`, // 20% opacity
                        color: classItem.color,
                        border: `1px solid ${classItem.color}40`, // 40% opacity border
                      }}
                    >
                      {classItem.name}
                    </div>
                  ))}
                </div>
              </ClassDisplayCard>
            </SectionContent>
          </Section>
        )}

        {/* Training Data */}
        <Section>
          <SectionTitle>
            <Database size={18} />
            Training & Validation Data
          </SectionTitle>
          <SectionContent>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              {/* Training Dataset */}
              <div style={{ flex: '1', minWidth: '300px' }}>
                <DatasetCard $type="training">
                  <div className="dataset-header">
                    <div className="dataset-indicator" />
                    <div className="dataset-title">Training Dataset</div>
                  </div>
                  <div
                    style={{
                      fontSize: '0.875rem',
                      color: 'rgba(255, 255, 255, 0.7)',
                      marginBottom: '0.75rem',
                    }}
                  >
                    Sources:{' '}
                    {fullDatasetInfo.training.length > 0
                      ? fullDatasetInfo.training.join(', ')
                      : 'No training datasets configured'}
                  </div>
                  <div className="dataset-stats">
                    <div className="stat-item">
                      <div className="stat-value">
                        {fullDatasetInfo.trainImageCount > 0
                          ? fullDatasetInfo.trainImageCount.toLocaleString()
                          : 'N/A'}
                      </div>
                      <div className="stat-label">Images</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-value">
                        {fullDatasetInfo.trainAnnotations > 0
                          ? fullDatasetInfo.trainAnnotations.toLocaleString()
                          : 'N/A'}
                      </div>
                      <div className="stat-label">Annotations</div>
                    </div>
                  </div>
                </DatasetCard>
              </div>

              {/* Validation Dataset */}
              <div style={{ flex: '1', minWidth: '300px' }}>
                {fullDatasetInfo.validation.length > 0 ? (
                  <DatasetCard $type="validation">
                    <div className="dataset-header">
                      <div className="dataset-indicator" />
                      <div className="dataset-title">Validation Dataset</div>
                    </div>
                    <div
                      style={{
                        fontSize: '0.875rem',
                        color: 'rgba(255, 255, 255, 0.7)',
                        marginBottom: '0.75rem',
                      }}
                    >
                      Sources: {fullDatasetInfo.validation.join(', ')}
                    </div>
                    <div className="dataset-stats">
                      <div className="stat-item">
                        <div className="stat-value">
                          {fullDatasetInfo.validationImageCount > 0
                            ? fullDatasetInfo.validationImageCount.toLocaleString()
                            : 'N/A'}
                        </div>
                        <div className="stat-label">Images</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-value">
                          {fullDatasetInfo.validationAnnotations > 0
                            ? fullDatasetInfo.validationAnnotations.toLocaleString()
                            : 'N/A'}
                        </div>
                        <div className="stat-label">Annotations</div>
                      </div>
                    </div>
                  </DatasetCard>
                ) : (
                  <DatasetCard $type="validation" style={{ opacity: 0.6 }}>
                    <div className="dataset-header">
                      <div className="dataset-indicator" />
                      <div className="dataset-title">Validation Dataset</div>
                    </div>
                    <div
                      style={{
                        fontSize: '0.875rem',
                        color: 'rgba(255, 255, 255, 0.5)',
                        marginBottom: '0.75rem',
                      }}
                    >
                      No validation datasets configured
                    </div>
                    <div className="dataset-stats">
                      <div className="stat-item">
                        <div className="stat-value">N/A</div>
                        <div className="stat-label">Images</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-value">N/A</div>
                        <div className="stat-label">Annotations</div>
                      </div>
                    </div>
                  </DatasetCard>
                )}
              </div>
            </div>
          </SectionContent>
        </Section>

        {/* Activity Timeline */}
        <Section>
          <SectionTitle>
            <Clock size={18} />
            Activity Timeline
          </SectionTitle>
          <SectionContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* Model Created */}
              <div
                style={{
                  background: 'transparent',
                  padding: '1.25rem',
                  borderRadius: '10px',
                  border: '1px solid rgba(128, 0, 133, 0.3)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '0.5rem',
                  }}
                >
                  <div
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      background: '#800085',
                      border: '2px solid rgba(128, 0, 133, 0.5)',
                    }}
                  />
                  <strong style={{ color: '#ffffff', fontSize: '0.95rem' }}>Model Created</strong>
                </div>
                <div
                  style={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontWeight: '500',
                    marginLeft: '1.75rem',
                  }}
                >
                  {new Date(model.created_at).toLocaleString()}
                </div>
              </div>

              {/* Training Started */}
              {model.train_started_at && (
                <div
                  style={{
                    background: 'transparent',
                    padding: '1.25rem',
                    borderRadius: '10px',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      marginBottom: '0.5rem',
                    }}
                  >
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        background: '#10b981',
                        border: '2px solid rgba(16, 185, 129, 0.5)',
                      }}
                    />
                    <strong style={{ color: '#ffffff', fontSize: '0.95rem' }}>
                      Training Started
                    </strong>
                  </div>
                  <div
                    style={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontWeight: '500',
                      marginLeft: '1.75rem',
                    }}
                  >
                    {new Date(model.train_started_at).toLocaleString()}
                  </div>
                </div>
              )}

              {/* Training Completed */}
              {model.train_ended_at && model.train_ended_at !== '0001-01-01T00:00:00Z' && (
                <div
                  style={{
                    background: 'transparent',
                    padding: '1.25rem',
                    borderRadius: '10px',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      marginBottom: '0.5rem',
                    }}
                  >
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        background: '#10b981',
                        border: '2px solid rgba(16, 185, 129, 0.5)',
                      }}
                    />
                    <strong style={{ color: '#ffffff', fontSize: '0.95rem' }}>
                      Training Completed
                    </strong>
                  </div>
                  <div
                    style={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontWeight: '500',
                      marginLeft: '1.75rem',
                    }}
                  >
                    {new Date(model.train_ended_at).toLocaleString()}
                  </div>
                </div>
              )}

              {/* Deployed */}
              {model.deployment && isDeployed && (
                <div
                  style={{
                    background: 'transparent',
                    padding: '1.25rem',
                    borderRadius: '10px',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      marginBottom: '0.5rem',
                    }}
                  >
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        background: '#3b82f6',
                        border: '2px solid rgba(59, 130, 246, 0.5)',
                      }}
                    />
                    <strong style={{ color: '#ffffff', fontSize: '0.95rem' }}>Deployed</strong>
                  </div>
                  <div
                    style={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontWeight: '500',
                      marginLeft: '1.75rem',
                    }}
                  >
                    {model.deployment?.deployed_at
                      ? new Date(model.deployment.deployed_at).toLocaleString()
                      : 'Currently active and serving predictions'}
                  </div>
                </div>
              )}
            </div>
          </SectionContent>
        </Section>

        {/* Model Lineage */}
        {(model.parent_id || modelsData?.models?.some((m: any) => m.parent_id === model.id)) && (
          <Section>
            <SectionTitle>
              <GitBranch size={18} />
              Model Lineage
            </SectionTitle>
            <SectionContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Parent Model */}
                {model.parent_id && (
                  <div
                    style={{
                      background: 'transparent',
                      padding: '1.25rem',
                      borderRadius: '10px',
                      border: '1px solid rgba(128, 0, 133, 0.3)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        marginBottom: '0.5rem',
                      }}
                    >
                      <div
                        style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          background: '#800085',
                          border: '2px solid rgba(128, 0, 133, 0.5)',
                        }}
                      />
                      <strong style={{ color: '#ffffff', fontSize: '0.95rem' }}>
                        Parent Model
                      </strong>
                    </div>
                    <div style={{ marginLeft: '1.75rem' }}>
                      {(() => {
                        const parentModel = modelsData?.models?.find(
                          (m: any) => m.id === model.parent_id
                        )
                        return parentModel ? (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() =>
                              navigate(`/models/${projectId}/${model.parent_id}/overview`)
                            }
                            style={{ fontSize: '0.875rem' }}
                          >
                            {parentModel.name}
                          </Button>
                        ) : (
                          <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Unknown</span>
                        )
                      })()}
                    </div>
                  </div>
                )}

                {/* Children Models - Show first 3, then "x more" */}
                {(() => {
                  const childrenModels =
                    modelsData?.models?.filter((m: any) => m.parent_id === model.id) || []
                  const displayedChildren = childrenModels.slice(0, 3)
                  const remainingCount = childrenModels.length - 3

                  return (
                    <>
                      {displayedChildren.map((child: any) => (
                        <div
                          key={child.id}
                          style={{
                            background: 'transparent',
                            padding: '1.25rem',
                            borderRadius: '10px',
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.5rem',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem',
                              marginBottom: '0.5rem',
                            }}
                          >
                            <div
                              style={{
                                width: '12px',
                                height: '12px',
                                borderRadius: '50%',
                                background: '#3b82f6',
                                border: '2px solid rgba(59, 130, 246, 0.5)',
                              }}
                            />
                            <strong style={{ color: '#ffffff', fontSize: '0.95rem' }}>
                              Child Model
                            </strong>
                          </div>
                          <div style={{ marginLeft: '1.75rem' }}>
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => navigate(`/models/${projectId}/${child.id}/overview`)}
                              style={{ fontSize: '0.875rem' }}
                            >
                              {child.name}
                            </Button>
                          </div>
                        </div>
                      ))}
                      {remainingCount > 0 && (
                        <div
                          style={{
                            background: 'transparent',
                            padding: '1.25rem',
                            borderRadius: '10px',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            textAlign: 'center',
                          }}
                        >
                          <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.95rem' }}>
                            +{remainingCount} more {remainingCount === 1 ? 'model' : 'models'}
                          </div>
                        </div>
                      )}
                    </>
                  )
                })()}
              </div>
            </SectionContent>
          </Section>
        )}
      </OverviewContainer>
    )
  }

  const renderTabContent = () => {
    if (!model) return null

    switch (activeTab) {
      case 'overview':
        return renderOverviewContent()
      case 'training-performance':
        return <CombinedTrainingPerformanceView modelId={modelId} projectId={projectId} />
      case 'deployment':
        return <ModelDeploymentView modelId={modelId} projectId={projectId} />
      case 'content':
        return <ModelDatasetsView modelId={modelId} projectId={projectId} />
      case 'model-versions':
        return <ModelVersionsView modelId={modelId} projectId={projectId} />
      case 'compare':
        return <ModelComparisonView />
      case 'settings':
        return renderSettingsContent()
      default:
        return null
    }
  }

  if (modelLoading || projectLoading) {
    return (
      <DetailContainer>
        <LoadingContainer>
          <Spinner animation="border" variant="primary" />
          <p>Loading model details...</p>
        </LoadingContainer>
      </DetailContainer>
    )
  }

  if (modelError || !model) {
    return (
      <DetailContainer>
        <ErrorContainer>
          <XCircle size={48} />
          <h3>Model Not Found</h3>
          <p>The requested model could not be found.</p>
          <Button variant="primary" onClick={handleBackClick}>
            Back to Models
          </Button>
        </ErrorContainer>
      </DetailContainer>
    )
  }

  const isDeployed = model.deployment?.status?.toLowerCase() === 'in_service'

  // Tab items
  const tabItems = [
    {
      key: 'overview',
      label: 'Overview',
      icon: <BarChart3 size={20} />,
    },
    {
      key: 'training-performance',
      label: 'Training & Performance',
      icon: <Zap size={20} />,
    },
    {
      key: 'deployment',
      label: 'Deployment',
      icon: <Rocket size={20} />,
    },
    {
      key: 'content',
      label: 'Content',
      icon: <Database size={20} />,
    },
    {
      key: 'model-versions',
      label: 'Versions',
      icon: <GitBranch size={20} />,
    },
    {
      key: 'compare',
      label: 'Compare',
      icon: <ArrowLeftRight size={20} />,
    },
    {
      key: 'settings',
      label: 'Settings',
      icon: <Settings size={20} />,
    },
  ]

  return (
    <div>
      {/* Back Button */}
      <div className="mb-3">
        <BackButton onClick={handleBackClick}>
          <ArrowLeft size={18} />
          Back to All Models
        </BackButton>
      </div>

      {/* Model Identification Header */}
      {model && (
        <div className="mb-4">
          <div className="d-flex align-items-center justify-content-between">
            {/* Left side - Icon and basic info */}
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center justify-content-center"
                style={{
                  width: '48px',
                  height: '48px',
                  background:
                    project?.annotation_type === 'classification'
                      ? 'linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)'
                      : 'linear-gradient(135deg, #059669 0%, #10B981 100%)',
                  borderRadius: '12px',
                  color: 'white',
                }}
              >
                <Brain size={24} />
              </div>
              <div>
                {isEditingName ? (
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <Form.Control
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      style={{
                        fontSize: '1.5rem',
                        fontWeight: '600',
                        color: '#ffffff',
                        background: 'transparent',
                        border: '1px solid #404040',
                        borderRadius: '6px',
                        padding: '0.5rem',
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveName()
                        } else if (e.key === 'Escape') {
                          handleCancelEditName()
                        }
                      }}
                      autoFocus
                    />
                    <Button
                      variant="success"
                      size="sm"
                      onClick={handleSaveName}
                      disabled={isUpdatingName || !editedName.trim()}
                      style={{ padding: '0.25rem 0.5rem' }}
                    >
                      {isUpdatingName ? <Spinner size="sm" /> : <Check size={16} />}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleCancelEditName}
                      disabled={isUpdatingName}
                      style={{ padding: '0.25rem 0.5rem' }}
                    >
                      <X size={16} />
                    </Button>
                  </div>
                ) : (
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <h2
                      style={{ fontSize: '1.5rem', fontWeight: '600', color: '#ffffff', margin: 0 }}
                    >
                      {model.name.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </h2>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleStartEditName}
                      style={{ padding: '0.25rem 0.5rem' }}
                      title="Edit model name"
                    >
                      <Edit2 size={16} />
                    </Button>
                  </div>
                )}
                <p className="mb-0" style={{ fontSize: '0.9rem', color: '#e0e0e0' }}>
                  {model.description ||
                    `AI model for ${
                      project?.annotation_type
                        ?.replace(/_/g, ' ')
                        .replace(/\b\w/g, (l) => l.toUpperCase()) || 'Classification'
                    } tasks`}
                </p>
              </div>
            </div>

            {/* Right side - Status, project info, and metadata */}
            <div className="d-flex align-items-center gap-3">
              <div className="d-flex flex-column align-items-end">
                <div className="d-flex align-items-center gap-2 mb-1">
                  <Badge
                    bg={
                      model.state === 'TRAINED'
                        ? 'success'
                        : model.state === 'TRAINING'
                        ? 'warning'
                        : model.state === 'ERR'
                        ? 'danger'
                        : 'secondary'
                    }
                    className="px-2 py-1"
                  >
                    {model.state === 'TRAINED'
                      ? 'Trained'
                      : model.state === 'TRAINING'
                      ? 'Training'
                      : model.state === 'ERR'
                      ? 'Failed'
                      : model.state === 'INITIALIZED'
                      ? 'Initialized'
                      : model.state === 'STOPPING'
                      ? 'Stopping'
                      : model.state === 'UNKNOWN'
                      ? 'Unknown'
                      : model.state}
                  </Badge>
                  {project && (
                    <Badge bg="secondary" className="px-2 py-1">
                      {project.annotation_type
                        ?.replace(/_/g, ' ')
                        .replace(/\b\w/g, (l) => l.toUpperCase()) || 'Classification'}
                    </Badge>
                  )}
                </div>
                <div className="d-flex flex-column align-items-end">
                  {project && (
                    <span style={{ fontSize: '0.85rem', color: '#d0d0d0' }}>
                      Project: {project.name}
                    </span>
                  )}
                  <span style={{ fontSize: '0.85rem', color: '#d0d0d0' }}>
                    Created {new Date(model.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs Content */}
      <PageWithTabs
        tabs={tabItems}
        activeTab={activeTab}
        onTabSelect={(k) => setActiveTab(k as ModelDetailTab)}
        tabContent={
          <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k as ModelDetailTab)}>
            <Tab.Content>
              <Tab.Pane eventKey="overview">{renderOverviewContent()}</Tab.Pane>
              <Tab.Pane eventKey="training-performance">
                <CombinedTrainingPerformanceView modelId={modelId} projectId={projectId} />
              </Tab.Pane>
              <Tab.Pane eventKey="deployment">
                <ModelDeploymentView
                  modelId={modelId}
                  projectId={projectId}
                  isDeployingModel={isDeployingModel}
                  onDeployModel={async (modelId: string) => {
                    try {
                      await deployModel({ id: modelId }).unwrap()
                    } catch (error: any) {
                      const message = (error as { data?: { message?: string } })?.data?.message
                      setError(message || 'Failed to deploy model')
                    }
                  }}
                />
              </Tab.Pane>
              <Tab.Pane eventKey="content">
                <ModelDatasetsView modelId={modelId} projectId={projectId} />
              </Tab.Pane>
              <Tab.Pane eventKey="model-versions">
                <ModelVersionsView modelId={modelId} projectId={projectId} />
              </Tab.Pane>
              <Tab.Pane eventKey="compare">
                <ModelComparisonView modelId={modelId} projectId={projectId} />
              </Tab.Pane>
              <Tab.Pane eventKey="settings">{renderSettingsContent()}</Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        }
        children={null}
      />

      {/* Confirmation Dialogs */}
      <ConfirmDialog
        show={!!showDeleteDeploymentConfirm}
        onHide={() => setShowDeleteDeploymentConfirm(null)}
        title="Remove Deployment"
        message="Are you sure you want to remove this model's deployment? This will stop the model from serving predictions."
        confirmRightText="Remove Deployment"
        cancelText="Cancel"
        onConfirmRight={() => handleDeleteDeployment()}
        variant="danger"
      />

      <ConfirmDialog
        show={!!showDeleteModelConfirm}
        onHide={() => setShowDeleteModelConfirm(null)}
        title="Delete Model"
        message="Are you sure you want to delete this model? This action cannot be undone and will permanently remove the model and all its data."
        confirmRightText="Delete Model"
        cancelText="Cancel"
        onConfirmRight={() => handleDeleteModel()}
        variant="danger"
      />

      <ErrorDialog show={!!error} onHide={() => setError(null)} message={error || ''} />
    </div>
  )
}

export default ModelDetailView
